import { CONFIG } from '../config';
import type { PowerUpState, PowerUpType } from '../types';
import type { BulletSpawner } from './BulletSpawner';
import type { OneWayBulletSpawner } from './OneWayBulletSpawner';
import type { Item } from '../entities/Item';

/**
 * 파워업 상태 관리
 */
export class PowerUpManager {
    private activePowerUps: Map<PowerUpType, PowerUpState> = new Map();
    private onBombActivated: (() => void) | null = null;

    /**
     * 번개 파워업 활성화 - 플레이어 속도 1.5배
     */
    activateLightning(): void {
        this.activePowerUps.set('lightning', {
            type: 'lightning',
            remainingTime: CONFIG.POWERUP.LIGHTNING.DURATION,
            maxDuration: CONFIG.POWERUP.LIGHTNING.DURATION
        });
    }

    /**
     * 시계 파워업 활성화 - 총알 속도 0.5배
     */
    activateClock(): void {
        this.activePowerUps.set('clock', {
            type: 'clock',
            remainingTime: CONFIG.POWERUP.CLOCK.DURATION,
            maxDuration: CONFIG.POWERUP.CLOCK.DURATION
        });
    }

    /**
     * 자석 파워업 활성화 - 아이템 자동 흡인
     */
    activateMagnet(): void {
        this.activePowerUps.set('magnet', {
            type: 'magnet',
            remainingTime: CONFIG.POWERUP.MAGNET.DURATION,
            maxDuration: CONFIG.POWERUP.MAGNET.DURATION
        });
    }

    /**
     * 폭탄 파워업 활성화 - 모든 총알/단방향 총알 제거
     */
    activateBomb(bulletSpawner: BulletSpawner, oneWayBulletSpawner?: OneWayBulletSpawner): void {
        bulletSpawner.clearAllBullets();
        if (oneWayBulletSpawner) {
            oneWayBulletSpawner.clearAll();
        }
        if (this.onBombActivated) {
            this.onBombActivated();
        }
    }

    /**
     * 폭탄 활성화 콜백 설정
     */
    setOnBombActivated(callback: () => void): void {
        this.onBombActivated = callback;
    }

    /**
     * 업데이트 - 파워업 타이머 감소
     */
    update(deltaTime: number): void {
        for (const [type, state] of this.activePowerUps) {
            state.remainingTime -= deltaTime;
            if (state.remainingTime <= 0) {
                this.activePowerUps.delete(type);
            }
        }
    }

    /**
     * 자석 효과로 아이템 이동
     */
    applyMagnetEffect(playerX: number, playerY: number, items: Item[], deltaTime: number): void {
        if (!this.isMagnetActive()) return;

        const range = CONFIG.POWERUP.MAGNET.RANGE;
        const speed = CONFIG.POWERUP.MAGNET.ATTRACTION_SPEED;

        for (const item of items) {
            // heart, star만 흡인
            if (item.type !== 'heart' && item.type !== 'star') continue;

            const dx = playerX - item.x;
            const dy = playerY - item.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance <= range && distance > 0) {
                // 플레이어 방향으로 이동
                const moveX = (dx / distance) * speed * deltaTime;
                const moveY = (dy / distance) * speed * deltaTime;
                item.x += moveX;
                item.y += moveY;
            }
        }
    }

    /**
     * 플레이어 속도 배율 반환
     */
    getSpeedMultiplier(): number {
        if (this.activePowerUps.has('lightning')) {
            return CONFIG.POWERUP.LIGHTNING.SPEED_MULT;
        }
        return 1.0;
    }

    /**
     * 총알 속도 배율 반환
     */
    getBulletSpeedMultiplier(): number {
        if (this.activePowerUps.has('clock')) {
            return CONFIG.POWERUP.CLOCK.BULLET_SPEED_MULT;
        }
        return 1.0;
    }

    /**
     * 자석 활성화 여부
     */
    isMagnetActive(): boolean {
        return this.activePowerUps.has('magnet');
    }

    /**
     * 번개 활성화 여부
     */
    isLightningActive(): boolean {
        return this.activePowerUps.has('lightning');
    }

    /**
     * 시계 활성화 여부
     */
    isClockActive(): boolean {
        return this.activePowerUps.has('clock');
    }

    /**
     * 활성 파워업 목록 반환
     */
    getActivePowerUps(): PowerUpState[] {
        return Array.from(this.activePowerUps.values());
    }

    /**
     * 특정 파워업 남은 시간 반환
     */
    getRemainingTime(type: PowerUpType): number {
        const state = this.activePowerUps.get(type);
        return state ? state.remainingTime : 0;
    }

    /**
     * 리셋
     */
    reset(): void {
        this.activePowerUps.clear();
    }
}
